import React, { useState, useEffect } from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import Router from "next/router";

import PrevButton from "components/PrevButton";
import NextButton from "components/NextButton";
import SelectInput from "components/SelectInput";
import Input from "components/Input";
import CustomFormatNumber from "components/CustomFormatNumber";
import Checkbox from "components/Checkbox";
import TwoCellRow from "components/TwoCellRow";
import CurrencyInput from "components/CurrencyInput";
import DatePickerField from "components/DatePickerField";
import { registerLocale } from "react-datepicker";
import { de, enUS } from "date-fns/locale";
import { ORDER_TYPE, RESPONSE_ERRORS } from "constants/index";
import cn from "classnames";
import * as yup from "yup";
import { withFormik } from "formik";
import { numberRegex } from "constants/validationRegex";
import { useFormattedMessage, useSideModalScrollToTop, calculationsFor1to8M } from "hooks";
import { splitDateStringForProperDate, getRegionFormattedDate, toGermanCurrency, toEnglishCurrency } from "utils";
import { initOrder } from "api";
import moment from "moment";
import ToolTip from "components/ToolTip";
import { selectLocale } from "store/app/selectors";
import { sendFormError } from "i18n/messages/errors";
import InvestmentChecks from "components/1to8MillionInvestments/InvestmentChecks";

const leftClass = "d-block";
const rightClass = "text-14 fw-600 d-block";

const nextDayDate = moment(new Date()).add(1, "days").format("YYYY-MM-DD");

const nextWeekDate = moment(new Date()).add(1, "weeks").format("YYYY-MM-DD");

const nextMonthDate = moment(new Date()).add(1, "months").format("YYYY-MM-DD");

const messages = {
    requiredField: "required_field",
    invalidShares: "invalid_shares",
    invalidPricePerShare: "invalid_price_per_share",
    highPricePerShare: "high_price_per_share",
    lowPricePerShare: "low_price_per_share",
    pleaseSelect: "please_select",
    soEditWalletMainHeading: "create_purchase_offer",
    boCreateHeading: "create_purchase_heading",
    boEditDigitalSharesLabel: "how_many_edit_digital_share_buy",
    boEditDigitalSharesPlaceholder: "digital_shares_numbers",
    boCreateUnitPriceLabel: "unit_price_label",
    boCreateUnitPriceSubLabel: "itv_label",
    boEditUnitPricePlaceholder: "price_per_share_placeholder",
    boCreateDateLabel: "date_offer_purchased",
    boEditDatePlaceholder: "purchase_offer_active_util",
    boCreateSplitHeading: "create_offer_split_heading",
    boCreateWholeOrderTitle: "leave_my_entire_offer",
    boCreateSplitOrderTitle1: "offer_given_to_several_sellers",
    boCreateSplitOrderTitle2: "shares",
    totalPurchasePrice: "total_purchase_price",
    boCreateUnitPriceSubLabelTooltip: "unit_price_sublabel_tooltip",
    minimumAmountOfShares: "minimum_amount_of_shares",
    sharesExceeds: "shares_exceeds",
    notEnoughBalance: "not_enough_balance",
    day: "one_day",
    week: "one_week",
    month: "one_month",
    other: "other",
    otherDate: "other_date",
    shareShortText: "pc",
    perPC: "per_pc",
    availableBestOfferTooltipText: "available_best_offer_tooltip_text",
    availableBestSalesOfferText: "available_best_sales_offer_text",
    noSalesOffersAvailable: "no_sales_offers_available",
    availableBestSalesOfferWarningText: "available_bestsales_offer_warning",
    investmnetChangedInMeantime: "update_your_information",
    investmentSumFormMax25k: "investment_sum_max25k",
    investmentSumFormMax50k: "investment_sum_max50k",
    investmentSumFormMax100k: "investment_sum_max100k",
    minInvestShares1: "min_invest_shares1",
    minInvestShares2: "min_invest_shares2",
    maxInvestAmountReached: "maximum_investment_amount_reached",
    interestedInHigherInvestment: "interested_in_higher_investment",
};

const NoValue = ({ locale, symbol = "€" }) => {
    return locale == "de" ? <>-,-- {symbol}</> : <>-.-- {symbol}</>;
};

const BuyOrderForm = ({
    className = "",
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    touched,
    errors,
    validateForm,
    isSubmitting,
    submitForm,
    setFieldValue,
    setFieldTouched,
    onBack,
    setCloseTitle,
    locale,
    payInAmount,
    status,
    projectTokenData = {},
    projectOffers = [],
    backSlideEffect = "",
    minShareTypeWise,
    maxInvestAmount,
    fetchInvestmentAmountData,
    investmentAmountData = 0,
    isInvestment1to8M,
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();

    const [isSplitAllowed, setSplitAllowed] = useState(false);
    const [totalPurchasePrice, setTotalPurchasePrice] = useState(0);
    const [isEnoughBalance, setIsEnoughBalance] = useState(true);
    const [requestError, setRequestError] = useState(false);
    const [valuationDate, setValuationDate] = useState("");
    const [tokenPriceWarning, setTokenPriceWarning] = useState("");
    const [isMobileScreen, setMobileScreen] = useState(false);
    const [investmnetChangedError, setInvestmnetChangedError] = useState("");
    const [showCheckboxes, setShowCheckboxes] = useState(false);

    const activeOrderDateOptions = [
        { value: nextDayDate, label: formatMessage(messages.day) },
        { value: nextWeekDate, label: formatMessage(messages.week) },
        { value: nextMonthDate, label: formatMessage(messages.month) },
        { value: "other", label: formatMessage(messages.otherDate) },
    ];

    useEffect(() => {
        setCloseTitle(formatMessage(messages.soEditWalletMainHeading));

        if (window.outerWidth < 768) {
            setMobileScreen(true);
        }

        // Prevent react datepicker input (Add readOnly)
        jQuery(".buy-order-form .react-datepicker__input-container input").attr("readOnly", true);
    }, []);

    useEffect(() => {
        if (status?.requestError == RESPONSE_ERRORS.SELF_DECLARATION_ERROR) {
            //RESPONSE_ERRORS.SELF_DECLARATION_ERROR
            fetchInvestmentAmountData?.();
            setInvestmnetChangedError("investmnetChangedInMeantime");
        } else {
            setInvestmnetChangedError("");
        }
    }, [status?.requestError]);

    useEffect(() => {
        locale === "en" ? registerLocale("en", enUS) : registerLocale("de", de);
    }, [locale]);

    useEffect(() => {
        if (values.buyShares && values.pricePerShare) {
            const total = parseInt(values.buyShares) * parseFloat(values.pricePerShare);
            setTotalPurchasePrice(total);
            total > payInAmount ? setIsEnoughBalance(false) : setIsEnoughBalance(true);
        }
    }, [values.buyShares, values.pricePerShare, payInAmount]);

    useEffect(() => {
        validateForm();
    }, [values, touched]);

    // To check the order can be split or not
    useEffect(() => {
        const inputBuyShares = parseInt(values.buyShares);

        inputBuyShares && (!errors.buyShares || errors.buyShares === undefined) && inputBuyShares >= minShareTypeWise * 2
            ? setSplitAllowed(true)
            : (setSplitAllowed(false), setFieldValue("splitOrder", false));
    }, [values.buyShares, errors.buyShares]);

    useEffect(() => {
        if (status?.requestError) {
            setRequestError(
                formatMessage(
                    status.requestError === "mangopay_beneficiary_account_not_available"
                        ? messages.mangopayBeneficiaryAccountNotAvailable
                        : sendFormError,
                ),
            );
        }
    }, [status]);

    useEffect(() => {
        if (projectTokenData?.valuationDateItv) {
            setValuationDate(
                getRegionFormattedDate(new Date(...splitDateStringForProperDate(projectTokenData.valuationDateItv))),
            );
        }
    }, [projectTokenData]);

    const minTokenPrice =
        projectTokenData?.indicativeTokenValue &&
        (parseFloat(projectTokenData.indicativeTokenValue).toFixed(2) * 0.75).toFixed(2);
    const maxTokenPrice =
        projectTokenData?.indicativeTokenValue &&
        (parseFloat(projectTokenData.indicativeTokenValue).toFixed(2) * 1.25).toFixed(2);

    useEffect(() => {
        values.pricePerShare < minTokenPrice
            ? setTokenPriceWarning(messages.lowPricePerShare)
            : values.pricePerShare > maxTokenPrice
              ? setTokenPriceWarning(messages.highPricePerShare)
              : setTokenPriceWarning("");
    }, [values.pricePerShare]);

    const nextDayDateNew = new Date().setDate(new Date().getDate() + 1);
    const next3MonthDateNew = new Date().setDate(new Date().getDate() + 90);

    const setCurrencyData = (formattedAmount, resetAmount) => {
        setFieldValue("intlAmount", formattedAmount);
        setFieldValue("pricePerShare", resetAmount);
        setFieldTouched("intlAmount", true);
    };

    useEffect(() => {
        if (isInvestment1to8M) {
            const totalAmount = values.buyShares * values.intlAmount + investmentAmountData;
            const { isMoreThan1k, isMoreThan10k } = calculationsFor1to8M(totalAmount, values, maxInvestAmount);
            setFieldValue("isMoreThan1k", isMoreThan1k);
            setFieldValue("isMoreThan10k", isMoreThan10k);
            setFieldValue("totalInvestmentAmount", totalAmount);

            setInvestmnetChangedError(null);

            // To show the checkboxes accroding to the entered digital shares
            values.buyShares >= minShareTypeWise && values.buyShares <= totalAmount
                ? setShowCheckboxes(true)
                : setShowCheckboxes(false);
        }
    }, [values.buyShares, values.pricePerShare, investmentAmountData]);

    useEffect(() => {
        if (isInvestment1to8M) {
            setFieldValue("moreThan100kFreelyDisposable", false);
            setFieldValue("notMoreThanDoubleMonthlyIncome", false);
        }
    }, [values.isMoreThan10k, values.isMoreThan1k]);

    //ADDITIONAL CHECKS FOR 1 TO 8 MILLION INVESTMENT
    const totalAmount = values.buyShares * values.intlAmount + investmentAmountData;
    const { isMoreThan1k, isMoreThan10k, isMoreThan25k, is1KConditionIncomeplete, is10kConditionIncomeplete } =
        calculationsFor1to8M(totalAmount, values, maxInvestAmount);

    const isButtonDisableWith1to8M = isInvestment1to8M
        ? is1KConditionIncomeplete || is10kConditionIncomeplete || isMoreThan25k
        : false;

    //CREATE ORDER CONDITIONS
    const isContinueButtonDisabled =
        isSubmitting ||
        Object.keys(errors).length ||
        !isEnoughBalance ||
        (isSplitAllowed && !values.singleOrder && !values.splitOrder) ||
        (values.buyActiveDate?.value === "other" && !values.buyActiveOtherDate) ||
        isButtonDisableWith1to8M ||
        investmentAmountData == maxInvestAmount;

    const sharesErrorMessage = (_minInvestShare) => {
        if (investmnetChangedError) {
            return formatMessage(messages.investmnetChangedInMeantime);
        }
        if (errors.totalInvestmentAmount) {
            return formatMessage(messages[errors.totalInvestmentAmount]);
        }
        if (errors.buyShares) {
            if (errors.buyShares == "minInvestShares") {
                return `${formatMessage(messages.minInvestShares1)} ${_minInvestShare} ${formatMessage(
                    messages.minInvestShares2,
                )}`;
            }
            return formatMessage(messages[errors.buyShares]);
        }
        return null;
    };

    return (
        <>
            <div className={`buy-order-form mb-20 ${className} ${backSlideEffect}`}>
                <h3>{formatMessage(messages.boCreateHeading)}</h3>
                <form method="post" onSubmit={handleSubmit}>
                    <Input
                        disabled={investmentAmountData == maxInvestAmount}
                        readOnly={investmentAmountData == maxInvestAmount}
                        label={formatMessage(messages.boEditDigitalSharesLabel)}
                        className="mb-8"
                        type="text"
                        name="buyShares"
                        placeholder={formatMessage(messages.boEditDigitalSharesPlaceholder)}
                        inputSymbol={formatMessage(messages.shareShortText)}
                        inputSymbolClass="black-color"
                        value={values.buyShares}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorMessage={sharesErrorMessage(minShareTypeWise)}
                        error={
                            investmnetChangedError || (touched.buyShares && (errors.buyShares || errors.totalInvestmentAmount))
                        }
                    />
                    {isInvestment1to8M &&
                        (investmentAmountData == maxInvestAmount || values.totalInvestmentAmount == maxInvestAmount) &&
                        (() => {
                            const isDataMax = investmentAmountData == maxInvestAmount;
                            const isTotalMax = values.totalInvestmentAmount == maxInvestAmount;
                            const labelClass = isDataMax ? "alert-red-color" : isTotalMax ? "light-grey-color" : "";

                            return (
                                <label className={`text-left mt-0 mb-2 ${labelClass}`}>
                                    {formatMessage(messages.maxInvestAmountReached)}
                                    {maxInvestAmount}
                                    {formatMessage(messages.interestedInHigherInvestment)}
                                </label>
                            );
                        })()}
                    <div className="mb-8">
                        <CurrencyInput
                            inputSymbolClass="black-color"
                            id="intlAmount"
                            name="intlAmount"
                            hiddenInputName="pricePerShare"
                            label={formatMessage(messages.boCreateUnitPriceLabel)}
                            sublabel={{
                                left: (
                                    <>
                                        <span className="flex mb-3">
                                            {formatMessage(messages.boCreateUnitPriceSubLabel)} {valuationDate}
                                            <span
                                                className="question d-inline-block text-center"
                                                data-tooltip-id="buyOrderForm"
                                                data-tooltip-content={formatMessage(messages.boCreateUnitPriceSubLabelTooltip)}
                                            >
                                                ?
                                            </span>
                                        </span>
                                        <span>
                                            {formatMessage(
                                                projectOffers[0]?.sellPrice
                                                    ? messages.availableBestSalesOfferText
                                                    : messages.noSalesOffersAvailable,
                                            )}{" "}
                                            <span
                                                data-place={isMobileScreen ? "right" : "top"}
                                                className="question d-inline-block text-center"
                                                data-tooltip-content={formatMessage(messages.availableBestOfferTooltipText)}
                                                data-tooltip-id="buyOrderForm"
                                            >
                                                ?
                                            </span>
                                        </span>
                                    </>
                                ),
                                right: (
                                    <>
                                        <CustomFormatNumber
                                            className="d-block mb-3"
                                            value={projectTokenData?.indicativeTokenValue}
                                        />
                                        {projectOffers[0]?.sellPrice ? (
                                            <CustomFormatNumber value={projectOffers[0]?.sellPrice} />
                                        ) : (
                                            <NoValue />
                                        )}{" "}
                                        {formatMessage(messages.perPC)}
                                    </>
                                ),
                            }}
                            placeholder={formatMessage(messages.boEditUnitPricePlaceholder)}
                            onCurrencyInputChange={(formattedAmount, resetAmount) =>
                                setCurrencyData(formattedAmount, resetAmount)
                            }
                            values={values}
                            onBlur={handleBlur}
                            errorMessage={
                                errors.intlAmount
                                    ? formatMessage(messages[errors.intlAmount || errors.totalInvestmentAmount])
                                    : null
                            }
                            error={
                                (touched.intlAmount && (errors.intlAmount || errors.totalInvestmentAmount)) ||
                                investmnetChangedError
                            }
                        />
                        {parseFloat(values.pricePerShare) > parseFloat(projectOffers[0]?.sellPrice) && !errors.intlAmount && (
                            <label className="warning-label text-left mb-3">
                                {formatMessage(messages.availableBestSalesOfferWarningText)}
                            </label>
                        )}
                        {tokenPriceWarning && !errors.intlAmount && (
                            <label className="warning-label text-left">{formatMessage(tokenPriceWarning)}</label>
                        )}
                    </div>
                    <SelectInput
                        className="mb-8"
                        label={formatMessage(messages.boCreateDateLabel)}
                        maxMenuHeight={400}
                        isSearchable={false}
                        classNamePrefix="select"
                        name="buyActiveDate"
                        selectClassName="id-order-date-input black-color"
                        options={activeOrderDateOptions}
                        placeholder={formatMessage(messages.pleaseSelect)}
                        value={values.buyActiveDate}
                        onChange={(value) => {
                            setFieldValue("buyActiveDate", value);
                            value.value !== "other" && setFieldValue("buyActiveOtherDate", value.value);
                        }}
                        errorMessage={errors.buyActiveDate ? formatMessage(messages[errors.buyActiveDate]) : null}
                        error={touched.buyActiveDate && errors.buyActiveDate}
                    />
                    {values.buyActiveDate?.value === "other" ? (
                        <DatePickerField
                            isDateIcon
                            className="mb-8"
                            label={formatMessage(messages.otherDate)}
                            name="buyActiveOtherDate"
                            placeholder={formatMessage(messages.boEditDatePlaceholder)}
                            value={values.buyActiveOtherDate && moment(values.buyActiveOtherDate).format("DD.MM.YYYY")}
                            selected={
                                values.buyActiveOtherDate &&
                                (new Date(values.buyActiveOtherDate || values.buyActiveOtherDate.split("T")[0]) || new Date())
                            }
                            onChange={(value) => {
                                setFieldValue("buyActiveOtherDate", value);
                                setFieldTouched("buyActiveOtherDate", true);
                            }}
                            onBlur={() => setFieldTouched("buyActiveOtherDate", true)}
                            minDate={nextDayDateNew}
                            maxDate={next3MonthDateNew}
                            peekNextMonth
                            locale={locale}
                            showMonthDropdown
                            dateFormat="MM-dd-yyyy"
                            showYearDropdown
                            errorMessage={errors.buyActiveOtherDate ? formatMessage(messages[errors.buyActiveOtherDate]) : null}
                            error={touched.buyActiveOtherDate && errors.buyActiveOtherDate}
                        />
                    ) : null}
                    {isSplitAllowed ? (
                        <>
                            <label className="mb-2 d-inline-block">{formatMessage(messages.boCreateSplitHeading)}</label>
                            <div className="order-type-actions mb-8">
                                <div className="d-flex p-4">
                                    <Checkbox
                                        inputId="singleOrder"
                                        name="singleOrder"
                                        value={values.singleOrder}
                                        label={formatMessage(messages.boCreateWholeOrderTitle)}
                                        onChange={() => {
                                            setFieldValue("splitOrder", false);
                                            setFieldValue("singleOrder", !values.singleOrder);
                                        }}
                                        isError={touched.singleOrder && errors.singleOrder}
                                    />
                                    <div className="d-flex justify-end flex-1">
                                        <i className="fas fa-user" aria-hidden="true" />
                                    </div>
                                </div>
                                <div className="d-flex p-4">
                                    <Checkbox
                                        inputId="splitOrder"
                                        name="splitOrder"
                                        value={values.splitOrder}
                                        label={`${formatMessage(messages.boCreateSplitOrderTitle1)}
                                        ${minShareTypeWise}
                                        ${formatMessage(messages.boCreateSplitOrderTitle2)}`}
                                        onChange={() => {
                                            setFieldValue("singleOrder", false);
                                            setFieldValue("splitOrder", !values.splitOrder);
                                        }}
                                        isError={touched.splitOrder && errors.splitOrder}
                                    />
                                    <div className="d-flex justify-end flex-1">
                                        <i className="fas fa-users" aria-hidden="true" />
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null}
                </form>
                <TwoCellRow
                    removeLClass
                    removeRClass
                    lClassname={leftClass}
                    rClassname={rightClass}
                    left={formatMessage(messages.totalPurchasePrice)}
                    right={<CustomFormatNumber value={totalPurchasePrice} />}
                />
                {(isMoreThan10k || isMoreThan1k) && isInvestment1to8M && showCheckboxes && (
                    <InvestmentChecks
                        moreThan100kFreelyDisposable={values.moreThan100kFreelyDisposable}
                        notMoreThanDoubleMonthlyIncome={values.notMoreThanDoubleMonthlyIncome}
                        isMoreThan10k={isMoreThan10k}
                        isMoreThan1k={isMoreThan1k}
                        setFieldValue={setFieldValue}
                        isLoading={isSubmitting}
                    />
                )}
                {!isEnoughBalance ? (
                    <span className="alert-red-color d-block mt-2 text-14">{formatMessage(messages.notEnoughBalance)}</span>
                ) : requestError ? (
                    <span className="error pl-0">{requestError}</span>
                ) : null}
            </div>
            <div className="bottom-bar-container">
                <PrevButton disable={isSubmitting} onClick={onBack} />
                <NextButton
                    className={cn({
                        "disable-grey": isContinueButtonDisabled,
                    })}
                    disable={isContinueButtonDisabled}
                    onClick={() => submitForm()}
                    isLoading={isSubmitting}
                />
            </div>
            <ToolTip id="buyOrderForm" />
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    locale: selectLocale,
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        enableReinitialize: true,
        mapPropsToValues: (props) => {
            const priceValue =
                parseFloat(props.projectOffers[0]?.sellPrice) !== 0
                    ? props.projectOffers[0]?.sellPrice
                    : props.projectTokenData?.indicativeTokenValue;
            const priceData =
                priceValue &&
                (props.locale === "en"
                    ? toEnglishCurrency(parseFloat(priceValue).toFixed(2))
                    : toGermanCurrency(parseFloat(priceValue).toFixed(2)));
            return {
                pricePerShare: priceData?.resetAmount || 0.0,
                intlAmount: priceData?.formattedAmount || "",
                buyShares: "",
                buyActiveDate: "",
                buyActiveOtherDate: "",
                singleOrder: false,
                splitOrder: false,
                totalInvestmentAmount: 0,
                isMoreThan1k: false,
                isMoreThan10k: false,
                moreThan100kFreelyDisposable: false,
                notMoreThanDoubleMonthlyIncome: false,
                ...(props.stepData && Object.keys(props.stepData).length ? props.stepData : {}),
            };
        },
        validateOnBlur: true,
        validateOnChange: true,
        validationSchema: (props) =>
            yup.object().shape({
                totalInvestmentAmount: yup
                    .string()
                    .test(
                        "totalInvestmentAmount",
                        `${
                            props?.maxInvestAmount === 50000
                                ? "investmentSumFormMax50k"
                                : props.maxInvestAmount === 100000
                                  ? "investmentSumFormMax100k"
                                  : "investmentSumFormMax25k"
                        }`,
                        (totalInvestmentAmount = 0) => {
                            return props?.isInvestment1to8M ? parseFloat(totalInvestmentAmount) <= props?.maxInvestAmount : true;
                        },
                    ),
                buyShares: yup
                    .string()
                    .matches(numberRegex, "invalidShares")
                    .required("requiredField")
                    .nullable(true)
                    .test("buyShares", "sharesExceeds", (value) => {
                        return !!(value && value <= props.projectTotalShares);
                    })
                    .test("buyShares", "minInvestShares", (value) => {
                        return !!(value && value >= props.minShareTypeWise);
                    }),
                intlAmount: yup
                    .string()
                    .required("requiredField")
                    .nullable(true)
                    .test("intlAmount", "invalidPricePerShare", (value) => {
                        const resetAmountData =
                            value && (props.locale === "en" ? toEnglishCurrency(value) : toGermanCurrency(value));
                        return !!(resetAmountData && resetAmountData.resetAmount && resetAmountData.resetAmount > 0);
                    }),
                buyActiveDate: yup.object().required("requiredField").nullable(true),
            }),
        handleSubmit: (values, { props, setSubmitting, setStatus }) => {
            try {
                setSubmitting(true);
                const additionalParams = values.isMoreThan1k
                    ? {
                          SelfDeclarationMoreThan100000FreelyDisposable: `${values.moreThan100kFreelyDisposable}`,
                          SelfDeclarationNotMoreThanDoubleMonthlyNetIncome: `${values.notMoreThanDoubleMonthlyIncome}`,
                      }
                    : values.isMoreThan10k
                      ? {
                            SelfDeclarationNotMoreThanDoubleMonthlyNetIncome: `${values.notMoreThanDoubleMonthlyIncome}`,
                        }
                      : {};

                const variables = {
                    Shares: values.buyShares,
                    SharePrice: values.pricePerShare,
                    ProjectID: Router.query.id,
                    TypeOrder: ORDER_TYPE.BUY,
                    IsSplitAllowed: values.splitOrder ? "true" : "false",
                    ExpirationDate:
                        values.buyActiveDate?.value === "other"
                            ? moment(new Date(values.buyActiveOtherDate)).format("YYYY-MM-DD")
                            : values.buyActiveDate?.value,
                    ...additionalParams,
                };

                initOrder(variables)
                    .then((res) => {
                        res && setSubmitting(false);

                        if (res.data.errors) {
                            const error = res.data.errors[0].message;
                            setStatus({ requestError: error });
                            return;
                        }

                        const order = res?.data?.data?.initOrder?.order;
                        values.buyPricePerShare = values.pricePerShare;
                        values.buyActiveOtherDate = moment(new Date(values.buyActiveOtherDate)).format("YYYY-MM-DD");

                        return props.onForward({ ...values, ...order });
                    })
                    .catch(() => {
                        setStatus({ requestError: true });
                        setSubmitting(false);
                    });
            } catch (error) {
                setStatus({ requestError: true });
                setSubmitting(false);
            }
        },
        displayName: "BuyOrderForm",
    }),
)(BuyOrderForm);
